import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '~/lib/utils';

interface AccordionContextValue {
  value: string[];
  onValueChange: (value: string[]) => void;
  type: 'single' | 'multiple';
}

const AccordionContext = React.createContext<AccordionContextValue | undefined>(undefined);

const useAccordion = () => {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error('Accordion components must be used within an Accordion');
  }
  return context;
};

interface AccordionProps {
  type?: 'single' | 'multiple';
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  children: React.ReactNode;
  className?: string;
}

export function Accordion({
  type = 'single',
  defaultValue = [],
  value: controlledValue,
  onValueChange,
  children,
  className,
}: AccordionProps) {
  const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);

  const value = controlledValue ?? internalValue;
  const handleValueChange = onValueChange ?? setInternalValue;

  const contextValue: AccordionContextValue = {
    value,
    onValueChange: handleValueChange,
    type,
  };

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={cn('space-y-4', className)}>{children}</div>
    </AccordionContext.Provider>
  );
}

interface AccordionItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function AccordionItem({ value, children, className }: AccordionItemProps) {
  const { value: openValues, onValueChange, type } = useAccordion();
  const isOpen = openValues.includes(value);

  const handleToggle = () => {
    if (type === 'single') {
      onValueChange(isOpen ? [] : [value]);
    } else {
      onValueChange(
        isOpen ? openValues.filter((v) => v !== value) : [...openValues, value]
      );
    }
  };

  return (
    <div className={cn('rounded-lg border border-border bg-muted/30', className)}>
      <AccordionItemContext.Provider value={{ isOpen, onToggle: handleToggle }}>
        {children}
      </AccordionItemContext.Provider>
    </div>
  );
}

interface AccordionItemContextValue {
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | undefined>(
  undefined
);

const useAccordionItem = () => {
  const context = React.useContext(AccordionItemContext);
  if (!context) {
    throw new Error('AccordionTrigger and AccordionContent must be used within an AccordionItem');
  }
  return context;
};

interface AccordionTriggerProps {
  children: React.ReactNode;
  className?: string;
}

export function AccordionTrigger({ children, className }: AccordionTriggerProps) {
  const { isOpen, onToggle } = useAccordionItem();

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex w-full items-center justify-between p-6 text-left transition hover:bg-muted/50',
        className
      )}
      aria-expanded={isOpen}
    >
      <span className="text-lg font-semibold text-foreground">{children}</span>
      <ChevronDown
        className={cn(
          'h-5 w-5 text-muted-foreground transition-transform duration-200',
          isOpen && 'rotate-180'
        )}
        aria-hidden
      />
    </button>
  );
}

interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
}

export function AccordionContent({ children, className }: AccordionContentProps) {
  const { isOpen } = useAccordionItem();

  if (!isOpen) return null;

  return (
    <div className={cn('border-t border-border px-6 pb-6 pt-4', className)}>
      <div className="text-sm text-muted-foreground">{children}</div>
    </div>
  );
}
